<template>
  <span>
    <span :id="id" class="popover-icon-trigger" tabindex="0" role="button">
      <slot name="icon">
        <InfoIcon />
      </slot>
    </span>

    <b-popover
      :target="id"
      :triggers="triggers"
      :placement="placement"
      custom-class="popover-icon-content"
    >
      <template v-if="title" #title>{{ title }}</template>
      <slot />
    </b-popover>
  </span>
</template>

<script>
import InfoIcon from "@/components/svg/InfoIcon.vue";

/**
 * PopoverIcon
 *
 * Ícone clicável que abre um `<b-popover>` com conteúdo totalmente
 * customizável. Genérico: não conhece o domínio de quem o usa — o
 * ícone e o conteúdo do balão são passados via slot.
 *
 * Uso:
 * ```html
 * <PopoverIcon :id="`minha-info-${item.id}`" title="Detalhes">
 *   <template #icon><MeuIcone /></template>
 *   Qualquer conteúdo aqui — texto, componentes, etc.
 * </PopoverIcon>
 * ```
 */
export default {
  name: "PopoverIcon",

  components: { InfoIcon },

  props: {
    /** Id único do elemento-gatilho (obrigatório — usado como `target` do popover). */
    id: {
      type: String,
      required: true,
    },
    /** Título exibido no topo do balão (opcional). */
    title: {
      type: String,
      default: null,
    },
    /** Posição do balão em relação ao ícone. */
    placement: {
      type: String,
      default: "left",
    },
    /** Eventos que abrem/fecham o balão (mesma API do `b-popover`). */
    triggers: {
      type: String,
      default: "click blur",
    },
  },
};
</script>

<style lang="scss" scoped>
.popover-icon-trigger {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  line-height: 1;
}
</style>

<style lang="scss">
/*
  b-popover renders its content outside this component's DOM (appended
  near <body>), so scoped styles can't reach it — this block is
  intentionally global, scoped down via the .popover-icon-content class
  set through the `custom-class` prop above.
*/
.popover-icon-content.popover {
  max-width: 340px;
}
</style>
